<template>
  <el-dialog
    :title="displayData.title"
    :visible="true"
    width="610px"
    :close-on-click-modal="false"
    @close="$emit('close')"
  >
    <ElInputWrapper>
      <BaseElForm ref="formRef" :model="formData" :rules="formRules" label-position="top">
        <FlexLayout col :gap="20">
          <GrayBlockContainer>
            <BaseElFormItem prop="minReservationInterval">
              <template #label>
                <FormItemTooltipLabel :label="displayData.minReservationInterval.label">
                  服務的準備時間要多久、能不能即時預約，必須在服務開始多久前預約。
                </FormItemTooltipLabel>
              </template>
              <FlexLayout :gap="8" col>
                <BaseElRadioGroup v-model="formData.minReservationInterval.mode" class="flex flex-col" style="gap: 8px;">
                  <BaseElRadio label="unlimit">無限制</BaseElRadio>
                  <BaseElRadio label="custom">
                    <FlexLayout :gap="8" align="center" inline>
                      顧客必須至少提前
                      <BaseElFormItem prop="minReservationInterval.value">
                        <BaseElInput
                          v-model="formData.minReservationInterval.value"
                          type="number"
                          :step="1"
                          testName="formData_minReservationInterval"
                          style="width: 78px;"
                        />
                      </BaseElFormItem>
                      <BaseElFormItem prop="minReservationInterval.timeUnit">
                        <TimeUnitSelect :value.sync="formData.minReservationInterval.timeUnit" style="width: 100px;" :allows="['minute', 'hour', 'day']" />
                      </BaseElFormItem>
                      預約服務
                    </FlexLayout>
                  </BaseElRadio>
                </BaseElRadioGroup>
                <div class="text-sub text-gray-100" style="line-height: 20px;">
                  {{ displayHint.minReservationInterval }}
                </div>
              </FlexLayout>
            </BaseElFormItem>

            <BaseElFormItem :label="displayData.maxReservationInterval.label" prop="maxReservationInterval">
              <template #label>
                <FormItemTooltipLabel :label="displayData.maxReservationInterval.label">
                  服務最早可以在什麼時候開始被預約、客人可以預約到多久以後的預約。
                </FormItemTooltipLabel>
              </template>
              <FlexLayout :gap="8" col>
                <BaseElRadioGroup v-model="formData.maxReservationInterval.mode" class="flex flex-col" style="gap: 8px;">
                  <BaseElRadio label="unlimit">無限制</BaseElRadio>
                  <BaseElRadio label="custom">
                    <FlexLayout :gap="8" align="center" inline>
                      僅開放預約未來
                      <BaseElFormItem prop="maxReservationInterval.value">
                        <BaseElInput
                          v-model="formData.maxReservationInterval.value"
                          testName="formData_maxReservationInterval"
                          type="number"
                          :step="1"
                          style="width: 78px;"
                        />
                      </BaseElFormItem>
                      <BaseElFormItem prop="maxReservationInterval.timeUnit">
                        <TimeUnitSelect
                          :value.sync="formData.maxReservationInterval.timeUnit"
                          style="width: 100px;"
                          :allows="['minute', 'hour', 'day']"
                        />
                      </BaseElFormItem>
                      內的服務
                    </FlexLayout>
                  </BaseElRadio>
                </BaseElRadioGroup>

                <div class="text-sub text-gray-100" style="line-height: 20px;">
                  {{ displayHint.maxReservationInterval }}
                </div>
              </FlexLayout>
            </BaseElFormItem>

            <BaseElFormItem :label="displayData.cancelInterval.label" prop="cancelInterval">
              <template #label>
                <FormItemTooltipLabel :label="displayData.cancelInterval.label">
                  預約後最晚在服務多久前可以取消，若無限制請輸入 0 即可。
                </FormItemTooltipLabel>
              </template>
              <FlexLayout :gap="8" col>
                <FlexLayout :gap="8" align="center" class="text-normal text-gray-100">
                  顧客可在預約時間開始前
                  <BaseElFormItem prop="cancelInterval.value">
                    <BaseElInput
                      v-model="formData.cancelInterval.value"
                      type="number"
                      :step="1"
                      style="width: 78px;"
                      testName="formData_cancelInterval"
                    />
                  </BaseElFormItem>
                  <BaseElFormItem prop="cancelInterval.timeUnit">
                    <TimeUnitSelect
                      :value.sync="formData.cancelInterval.timeUnit"
                      style="width: 100px;"
                      :allows="['minute', 'hour', 'day']"
                    />
                  </BaseElFormItem>
                  取消服務
                </FlexLayout>
                <div class="text-sub text-gray-100" style="line-height: 20px;">
                  {{ displayHint.cancelInterval }}
                </div>
              </FlexLayout>
            </BaseElFormItem>
          </GrayBlockContainer>

          <div>
            <BaseElFormItem label="根據您設定的綜合結果">
              <FlexLayout :gap="8" col class="text-sub" style="line-height: 19px;">
                <FlexLayout>
                  <p style="width: 200px">現在時間</p>
                  <p>{{ displayHint.summary.nowDateTime }}</p>
                </FlexLayout>
                <FlexLayout>
                  <p style="width: 200px">實際可預約時間</p>
                  <p class="text-primary-100">{{ displayHint.summary.appointmentRange }}</p>
                </FlexLayout>
                <FlexLayout>
                  <p style="width: 200px">預約取消期限</p>
                  <p class="text-primary-100">{{ displayHint.summary.cancelRange }}</p>
                </FlexLayout>
              </FlexLayout>
            </BaseElFormItem>

            <BaseElFormItem prop="enableCancelReasonCollection">
              <template #label>
                <FormItemTooltipLabel :label="displayData.enableCancelReasonCollection.label" :tooltipWidth="220">
                  {{ displayData.enableCancelReasonCollection.hint }}
                </FormItemTooltipLabel>
              </template>
              <BaseElSwitch
                v-model="formData.enableCancelReasonCollection"
                testName="formData_enableCancelReasonCollection"
                :active-text="displayData.switch.open"
                :inactive-text="displayData.switch.close"
              />
            </BaseElFormItem>

            <BaseElFormItem :label="displayData.enableClientCustomTimeSlots.label" prop="enableClientCustomTimeSlots">
              <BaseElSwitch
                v-model="formData.enableClientCustomTimeSlots"
                testName="formData_enableClientCustomTimeSlots"
                :active-text="displayData.switch.open"
                :inactive-text="displayData.switch.close"
                @change="onEnableClientCustomTimeSlotsChange"
              />
            </BaseElFormItem>

            <BaseElFormItem v-if="showControl.showPeriods" prop="displayPeriod" class="mb-[8px]">
              <template slot="label">
                <FormItemTooltipLabel :label="displayData.clientCustomTimeSlots.label" :tooltipWidth="200">
                  {{ displayData.clientCustomTimeSlots.tooltip }}
                </FormItemTooltipLabel>
              </template>
              <BaseElRadioGroup v-model="formData.displayPeriod" testName="formData_displayPeriod" @change="updateDisplayPeriod">
                <BaseElRadio
                  v-for="item in displayPeriodConfig"
                  :key="item.value"
                  :label="item.value"
                  :disabled="item.value === 'custom'"
                >
                  {{ item.label }}
                </BaseElRadio>
              </BaseElRadioGroup>
            </BaseElFormItem>
            <BaseElFormItem v-if="showControl.showPeriods" prop="clientCustomTimeSlots">
              <div class="time-slots" :class="`time-unit-${configData.timeUnit}`">
                <Tag
                  v-for="(time, index) in timeSlotsFlat"
                  :key="index"
                  :type="formData.clientCustomTimeSlots.includes(time) ? 'action' : 'info'"
                  class="time-slot-tag"
                  @click.native="toggleTimeSlot(time)"
                >
                  {{ time }}
                </Tag>
              </div>
              <p class="text-sm text-gray-80">{{ displayData.clientCustomTimeSlots.warning }}</p>
            </BaseElFormItem>
          </div>
        </FlexLayout>
      </BaseElForm>
    </ElInputWrapper>
    <div slot="footer">
      <BaseElButton plain :loading="loading" @click="$emit('close')">{{ displayData.btn.cancel }}</BaseElButton>
      <BaseElButton type="primary" data-cy="update-time-parameter" @click="onSubmit"> {{ displayData.btn.save }} </BaseElButton>
    </div>
  </el-dialog>
</template>

<script>
import { UpdateReservationParameter } from '@/api/reservation'
import { useBaseForm } from '@/use/useForm'
import { computed, defineComponent, onBeforeMount } from 'vue'
import { useShop } from '@/use/shop'
import notifyMessage from '@/config/notifyMessage'
import { integerRules, isDigitRules, noEmptyRules, rangeRules } from '@/validation'
import FormItemTooltipLabel from '@/components/Form/FormItemTooltipLabel.vue'
import { displayPeriodConfig } from '@/config/reservation'
import { i18n } from '@/plugins/i18n/i18n'
import { get } from 'lodash'
import { generateFormatPeriod } from '@/utils/time'
import GrayBlockContainer from '@/components/Container/GrayBlockContainer.vue'
import TimeUnitSelect from '@/components/Select/TimeUnitSelect.vue'
import ElInputWrapper from '@/components/ElInputWrapper.vue'
import { formatDate } from '@/utils/date'
import dayjs from '@/lib/dayjs'

export default defineComponent({
  name: 'EditReservationClientPeriodSettingsModal',
  components: {
    TimeUnitSelect,
    FormItemTooltipLabel,
    GrayBlockContainer,
    ElInputWrapper,
  },
  props: {
    configData: { type: Object, default: () => ({}) },
  },
  setup (props, { emit }) {
    const { formRef, initFormData, formData, checkForm, loading } = useBaseForm()
    const { shopId } = useShop()

    const formRules = computed(() => {
      const rules = {
        'minReservationInterval.value': [noEmptyRules(), isDigitRules(), rangeRules(-1, 525600), integerRules()],
        'maxReservationInterval.value': [noEmptyRules(), isDigitRules(), rangeRules(-1, 525600), integerRules()],
        'cancelInterval.value': [isDigitRules(), rangeRules(0, 9999999), integerRules()],
        displayPeriod: [noEmptyRules()],
        enableClientCustomTimeSlots: [noEmptyRules()],
      }

      if (formData.minReservationInterval.mode === 'unlimit') {
        delete rules['minReservationInterval.value']
      }
      if (formData.maxReservationInterval.mode === 'unlimit') {
        delete rules['maxReservationInterval.value']
      }
      return rules
    })
    const displayData = computed(() => {
      const data = {
        title: i18n.t('reservationClientPeriodSettings.title'),
        nowTime: formatDate(new Date(), 'YYYY/MM/DD HH:mm'),
        minReservationInterval: {
          label: i18n.t('reservationClientPeriodSettings.minReservationInterval.title'),
        },
        maxReservationInterval: {
          label: i18n.t('reservationClientPeriodSettings.maxReservationInterval.title'),
        },
        cancelInterval: {
          label: i18n.t('reservationClientPeriodSettings.cancelInterval.title'),
        },
        enableCancelReasonCollection: {
          label: i18n.t('reservationClientPeriodSettings.enableCancelReasonCollection.title'),
          hint: i18n.t('reservationClientPeriodSettings.enableCancelReasonCollection.hint'),
        },
        enableClientCustomTimeSlots: {
          label: i18n.t('reservationClientPeriodSettings.enableClientCustomTimeSlots.title'),
        },
        clientCustomTimeSlots: {
          label: i18n.t('reservationClientPeriodSettings.clientCustomTimeSlots.title'),
          tooltip: i18n.t('reservationClientPeriodSettings.clientCustomTimeSlots.tooltip'),
          warning: i18n.t('reservationClientPeriodSettings.enableClientCustomTimeSlots.warning.title'),
        },
        switch: {
          open: i18n.t('common.open.text'),
          close: i18n.t('common.close.text'),
        },
        btn: {
          cancel: i18n.t('common.button.cancel.text'),
          save: i18n.t('common.button.save.text'),
        },
      }
      return data
    })

    const displayHint = computed(() => {
      const now = new Date()
      const nowTime = formatDate(now, 'MM/DD HH:mm')
      const minReservationTime = dayjs(now).subtract(convertToMins(formData.minReservationInterval), 'minute')
      const maxReservationTime = dayjs(now).add(convertToMins(formData.maxReservationInterval), 'minute')
      const cancelTime = dayjs(now).subtract(formData.cancelInterval.value, formData.cancelInterval.timeUnit)

      const summaryStart = dayjs(now).add(convertToMins(formData.minReservationInterval), 'minute')

      const hint = {
        minReservationInterval: `依照設定：顧客如需預約 ${nowTime} 的服務，最晚需在 ${minReservationTime.format('MM/DD HH:mm')} 前完成預約 `,
        maxReservationInterval: `依照設定：現在是 ${nowTime}，則顧客最多可以預約到 ${maxReservationTime.format('MM/DD HH:mm')} 的服務`,
        cancelInterval: formData.cancelInterval.value === '0' || formData.cancelInterval.value === 0
          ? `依照設定：顧客已預約 ${nowTime} 的服務，若要取消預約，可隨時取消。`
          : `依照設定：顧客已預約 ${nowTime} 的服務，若要取消預約，最晚必須在 ${cancelTime.format('MM/DD HH:mm')} 前取消，超過後顧客無法取消預約。`,
        summary: {
          nowDateTime: dayjs(now).format('YYYY/MM/DD HH:mm'),
          appointmentRange: `顧客現在可預約 ${summaryStart.format('MM/DD HH:mm')} 到 ${maxReservationTime.format('MM/DD HH:mm')} 的服務`,
          cancelRange: formData.cancelInterval.value === '0' || formData.cancelInterval.value === 0
            ? `已預約 ${nowTime} 的服務，可隨時取消`
            : `已預約 ${nowTime} 的服務需在 ${cancelTime.format('MM/DD HH:mm')} 前取消`,
        },
      }

      if (formData.minReservationInterval.mode === 'unlimit') {
        hint.minReservationInterval = `依照設定：顧客如需預約 ${nowTime} 的服務，在服務開始前都可以完成預約。`
      }
      if (formData.maxReservationInterval.mode === 'unlimit') {
        hint.summary.appointmentRange = `顧客現在可預約 ${summaryStart.format('MM/DD HH:mm')} 後的服務`
        hint.maxReservationInterval = `依照設定：現在是 ${nowTime}，顧客可以預約未來已排班的所有服務。`
      }

      return hint
    })

    const convertToMins = (setting) => {
      if (!setting || !setting.value) {
        return 0
      }
      if (get(setting, 'mode') === 'unlimit') {
        return -1
      }
      switch (setting.timeUnit) {
      case 'minute':
        return setting.value
      case 'hour':
        return setting.value * 60
      case 'day':
        return setting.value * 1440
      default:
        return 0
      }
    }

    const showControl = computed(() => {
      const controls = {
        showPeriods: formData.enableClientCustomTimeSlots,
      }
      return controls
    })
    const timeSlotsFlat = computed(() => {
      const timeUnit = props.configData.timeUnit || 30
      const timeOffset = props.configData.timeUnitOffset || 0
      // const slots = map(generateNumbers(0, 1440, timeUnit + timeOffset), (i) => timesConvert(i))
      // return slots
      return generateFormatPeriod({
        gap: timeUnit,
        offset: timeOffset,
        maxEnd: 1439,
      })
    })

    const toggleTimeSlot = (time) => {
      const updatedSlots = [...formData.clientCustomTimeSlots]
      if (updatedSlots.includes(time)) {
        formData.clientCustomTimeSlots = updatedSlots.filter(t => t !== time)
      } else {
        updatedSlots.push(time)
        formData.clientCustomTimeSlots = updatedSlots
      }
      if (formData.clientCustomTimeSlots.length === timeSlotsFlat.value.length) {
        formData.displayPeriod = 'all'
      } else if (formData.clientCustomTimeSlots.length === 0) {
        formData.displayPeriod = 'empty'
      } else {
        formData.displayPeriod = 'custom'
      }
    }

    const updateDisplayPeriod = () => {
      if (formData.displayPeriod === 'all') {
        formData.clientCustomTimeSlots = [...timeSlotsFlat.value]
      } else if (formData.displayPeriod === 'empty') {
        formData.clientCustomTimeSlots = []
      }
    }

    const onEnableClientCustomTimeSlotsChange = () => {
      if (formData.enableClientCustomTimeSlots) {
        if (!formData.clientCustomTimeSlots.length) {
          formData.clientCustomTimeSlots = [...timeSlotsFlat.value]
          formData.displayPeriod = 'all'
        }
      }
    }

    const onSubmit = async () => {
      if (loading.value) return
      loading.value = true
      if (!(await checkForm(formRef.value))) {
        loading.value = false
        return
      }
      if (formData.enableClientCustomTimeSlots && formData.clientCustomTimeSlots.length === 0) {
        window.$message.error(i18n.t('reservationClientPeriodSettings.message.selectTimeSlot'))
        loading.value = false
        return
      }

      const payload = {
        shopId: shopId.value,
        ...props.configData,
        minReservationInterval: convertToMins(formData.minReservationInterval),
        maxReservationInterval: convertToMins(formData.maxReservationInterval),
        cancelInterval: convertToMins(formData.cancelInterval),
        enableCancelReasonCollection: formData.enableCancelReasonCollection,
        enableClientCustomTimeSlots: formData.enableClientCustomTimeSlots,
        clientCustomTimeSlots: formData.clientCustomTimeSlots,
      }

      if (formData.minReservationInterval.mode === 'unlimit') {
        payload.minReservationInterval = -1
      }
      if (formData.maxReservationInterval.mode === 'unlimit') {
        payload.maxReservationInterval = -1
      }

      const [, err] = await UpdateReservationParameter(payload)
      loading.value = false
      if (err) {
        window.$message.error(err)
        return
      }

      window.$message.success(notifyMessage.updateSuccess)
      emit('close')
      emit('refresh')
    }
    const periodType = (clientCustomTimeSlots) => {
      if (!clientCustomTimeSlots) return 'all'
      if (get(clientCustomTimeSlots, 'length') === timeSlotsFlat.value.length) {
        return 'all'
      } else if (get(clientCustomTimeSlots, 'length') === 0) {
        return 'empty'
      } else {
        return 'custom'
      }
    }

    const autoTimeUnit = (mins) => {
      if (mins % 1440 === 0) {
        return 'day'
      } else if (mins % 60 === 0) {
        return 'hour'
      } else {
        return 'minute'
      }
    }

    const autoTimeValue = (mins) => {
      if (mins === -1) return -1
      if (mins % 1440 === 0) {
        return mins / 1440
      } else if (mins % 60 === 0) {
        return mins / 60
      } else {
        return mins
      }
    }

    const syncFormData = () => {
      const displayPeriod = periodType(props.configData.clientCustomTimeSlots)
      initFormData({
        minReservationInterval: {
          mode: props.configData.minReservationInterval === -1 ? 'unlimit' : 'custom',
          timeUnit: autoTimeUnit(props.configData.minReservationInterval),
          value: props.configData.minReservationInterval >= 0 ? autoTimeValue(props.configData.minReservationInterval) : undefined,
        },
        maxReservationInterval: {
          mode: props.configData.maxReservationInterval === -1 ? 'unlimit' : 'custom',
          timeUnit: autoTimeUnit(props.configData.maxReservationInterval),
          value: props.configData.maxReservationInterval >= 0 ? autoTimeValue(props.configData.maxReservationInterval) : undefined,
        },
        cancelInterval: {
          timeUnit: autoTimeUnit(props.configData.cancelInterval),
          value: autoTimeValue(props.configData.cancelInterval),
        },
        enableCancelReasonCollection: props.configData.enableCancelReasonCollection,
        enableClientCustomTimeSlots: props.configData.enableClientCustomTimeSlots,
        displayPeriod,
        clientCustomTimeSlots: props.configData.clientCustomTimeSlots || [],
      })
    }

    onBeforeMount(() => {
      syncFormData()
    })

    return {
      formRef,
      onSubmit,
      loading,
      formRules,
      formData,
      displayPeriodConfig,
      timeSlotsFlat,
      toggleTimeSlot,
      updateDisplayPeriod,
      showControl,
      displayData,
      onEnableClientCustomTimeSlotsChange,
      displayHint,
    }
  },
})
</script>

<style lang="postcss" scoped>
.time-slots {
  @apply grid gap-[8px] grid-cols-[repeat(10,50px)];
  &.time-unit-10,
  &.time-unit-15,
  &.time-unit-30,
  &.time-unit-90 {
    @apply grid-cols-[repeat(8,50px)];
  }
  &.time-unit-60,
  &.time-unit-120 {
    @apply grid-cols-[repeat(6,50px)];
  }
}
.time-slot-tag {
  @apply h-[25px] leading-[1] text-[12px] px-[6px] py-[4px] cursor-pointer;
}
</style>
